import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { Divider } from '@spark-web/divider';
import { ChevronDownIcon, ChevronUpIcon } from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useTheme } from '@spark-web/theme';
import { CSSProperties, FC, Fragment, ReactNode, useState } from 'react';

import MenuLink from './menu-link';

interface MenuLinkItem {
  // URL to navigate to
  href: string;
  // Text to display in the menu
  text: string;
  // Icon to display next to the text
  icon: ReactNode;
}

interface ProfilePillProps {
  // Name of the user
  name: string;
  // URL to the user's profile picture
  picture: string;
  // Enterprise partner name or organization where user belongs to
  organization?: string;
  // Array of menu links
  menuLinks: MenuLinkItem[];
}

const ProfilePill: FC<ProfilePillProps> = ({ name, organization, picture, menuLinks }) => {
  const theme = useTheme();

  // Custom styling for the profile pill
  const customPillStyling = css({
    ':hover': {
      cursor: 'pointer',
      backgroundColor: theme.color.background.surfaceMuted,
    },
    ':active': {
      backgroundColor: theme.color.background.surfacePressed,
    },
    [`@media (min-width: ${theme.breakpoint.tablet}px)`]: {
      width: '186px',
    },
  });

  const profilePillTextStyling: CSSProperties = {
    textAlign: 'left',
  };

  // State to toggle the menu
  const [showMenu, setShowMenu] = useState(false);
  const ChevronIcon = showMenu ? ChevronUpIcon : ChevronDownIcon;

  return (
    <Box position="relative">
      <Box display="flex">
        <Box
          paddingY="small"
          display="flex"
          borderRadius="full"
          border="standard"
          alignItems="center"
          css={css(customPillStyling)}
          padding="small"
          height="large"
          onClick={() => setShowMenu(!showMenu)}
        >
          <Box
            display="flex"
            alignItems="center"
            css={css({
              maxWidth: '138px',
            })}
          >
            <Box
              as="img"
              src={picture}
              borderRadius="full"
              border="standard"
              width="medium"
              height="medium"
              alignItems="center"
              data={{
                testId: 'profile-pill-picture',
              }}
            ></Box>
            <Stack gap="medium" padding="small" overflow="hidden">
              <Text weight="semibold" size="xsmall" overflowStrategy="truncate" style={profilePillTextStyling}>
                {name}
              </Text>
              {organization && (
                <Text tone="muted" size="xsmall">
                  {organization}
                </Text>
              )}
            </Stack>
          </Box>
          <Box paddingRight="large" style={{ marginLeft: 'auto' }}>
            <ChevronIcon size="xxsmall" tone="muted" />
          </Box>
        </Box>
      </Box>

      {showMenu && (
        <Stack
          position="absolute"
          background="surface"
          right={0}
          marginTop="xsmall"
          border="standard"
          borderRadius="medium"
          shadow="large"
          padding="small"
          overflow="visible"
          gap="small"
          zIndex="dropdown"
          css={css({ minWidth: '245px' })}
        >
          {menuLinks.map((link, index) => (
            <Fragment key={`${link.text}-${index}`}>
              {index !== 0 && <Divider />}
              <MenuLink
                key={link.href}
                href={link.href}
                handleClick={() => setShowMenu(false)}
                gap="small"
                padding="small"
                borderRadius="small"
              >
                <Box display="flex" alignItems="center" gap="small">
                  <Box>{link.icon}</Box>
                  <Text size="small">{link.text}</Text>
                </Box>
              </MenuLink>
            </Fragment>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ProfilePill;
